/* eslint-disable complexity */
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@/components/ui/sheet';
import { AntragsContent } from '@/domain/navigation/benachrichtigungen/components/antragsSheet/AntragsContent';
import { BenachrichtigungenTrigger } from '@/domain/navigation/benachrichtigungen/components/desktop/BenachrichtigungenTrigger';
import AbwesenheitsantragDetail from '@/domain/navigation/benachrichtigungen/components/details/abwesenheitsantrag/AbwesenheitsantragDetail';
import DokumenteDetail from '@/domain/navigation/benachrichtigungen/components/details/dokumente/DokumenteDetail';
import FehlendeArbeitszeitDetail from '@/domain/navigation/benachrichtigungen/components/details/fehlendeArbeitszeit/FehlendeArbeitszeitDetail';
import FristenDetail from '@/domain/navigation/benachrichtigungen/components/details/fristen/FristenDetail';
import KrankmeldungenDetail from '@/domain/navigation/benachrichtigungen/components/details/krankmeldungen/KrankmeldungenDetail';
import PersonalfragebogenDetail from '@/domain/navigation/benachrichtigungen/components/details/personalfragebogen/PersonalfragebogenDetail';
import SchichttauschDetail from '@/domain/navigation/benachrichtigungen/components/details/schichttausch/SchichttauschDetail';
import { ListItem } from '@/domain/navigation/benachrichtigungen/components/mobile/ListItem/ListItem';
import { SubListItem } from '@/domain/navigation/benachrichtigungen/components/mobile/subListItem/SubListItem';
import { Benachrichtigungen } from '@/domain/navigation/benachrichtigungen/types/Benachrichtungen';
import { SchichttauschAntrag } from '@/domain/navigation/benachrichtigungen/types/SchichttauschAntrag';
import { getBenachrichtigungenCount } from '@/domain/navigation/benachrichtigungen/utils/getBenachrichtigungenCount';
import { useProfile } from '@/hooks';
import {
  faAlarmExclamation,
  faArrowLeftArrowRight,
  faCalendarXmark,
  faChevronLeft,
  faClock,
  faFile,
  faPaperPlane,
  faSuitcaseMedical,
  faXmarkLarge,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Transition } from '@headlessui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type BenachrichtungenOptions =
  | 'fehlendeArbeitszeiten'
  | 'abwesenheitsAntraege'
  | 'fristen'
  | 'krankmeldungen'
  | 'dokumente'
  | 'personalfragebogen'
  | 'schichttausch'
  | 'schichttauschAntrag';

// eslint-disable-next-line max-lines-per-function
export default function BenachrichtungenMobile({
  benachrichtigungen,
}: {
  benachrichtigungen: Benachrichtigungen;
}) {
  const { t } = useTranslation();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] =
    useState<BenachrichtungenOptions | null>(null);
  const { manager } = useProfile();
  const [activeAntrag, setActiveAntrag] = useState<SchichttauschAntrag | null>(
    null,
  );

  function handleClose() {
    setIsSheetOpen(false);
    setActiveSubMenu(null);
  }

  const count = getBenachrichtigungenCount(benachrichtigungen, manager);

  return (
    <Sheet
      open={isSheetOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        } else {
          setIsSheetOpen(true);
        }
      }}>
      <SheetTrigger asChild>
        <BenachrichtigungenTrigger count={count} active={count > 0} />
      </SheetTrigger>
      <SheetContent className="w-dvw [&>button:first-of-type]:hidden flex flex-col gap-4">
        <SheetHeader className="grid grid-cols-[1fr_auto_1fr] space-y-0 items-center">
          {activeSubMenu && (
            <Button
              variant="ghost"
              className="text-neutrals-800 "
              size="icon"
              onClick={() => setActiveSubMenu(null)}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
          )}
          <span className="col-start-2 w-fit justify-self-center">
            {t('domain.navigation.benachrichtigungen.title')}
          </span>
          <Button
            variant="ghost"
            className="text-neutrals-800 justify-self-end mt-0"
            size="icon"
            onClick={() => {
              setActiveSubMenu(null);
              setIsSheetOpen(false);
            }}>
            <FontAwesomeIcon icon={faXmarkLarge} />
          </Button>
        </SheetHeader>
        {!activeSubMenu ? (
          <div className="flex flex-col gap-4">
            {manager.rechte.arbeitszeit.bearbeiten &&
              manager.rechte.arbeitszeit.erlaubt &&
              benachrichtigungen.fehlendeArbeitszeiten.length > 0 && (
                <ListItem
                  onClick={() => setActiveSubMenu('fehlendeArbeitszeiten')}
                  count={benachrichtigungen.fehlendeArbeitszeiten.length}>
                  <FontAwesomeIcon icon={faClock} />
                  <p>
                    {t(
                      'domain.navigation.benachrichtigungen.fehlendeArbeitszeiten',
                    )}
                  </p>
                </ListItem>
              )}
            {manager.rechte.kalender &&
              benachrichtigungen.abwesenheitsantraege.length > 0 && (
                <ListItem
                  count={benachrichtigungen.abwesenheitsantraege.length}
                  onClick={() => setActiveSubMenu('abwesenheitsAntraege')}>
                  <FontAwesomeIcon icon={faCalendarXmark} />
                  <p>
                    {t(
                      'domain.navigation.benachrichtigungen.abwesenheitsantraege',
                    )}
                  </p>
                </ListItem>
              )}
            {manager.rechte.kalender &&
              benachrichtigungen.krankmeldungen.length > 0 && (
                <ListItem
                  count={benachrichtigungen.krankmeldungen.length}
                  onClick={() => setActiveSubMenu('krankmeldungen')}>
                  <FontAwesomeIcon icon={faSuitcaseMedical} />
                  <p>
                    {t(
                      'domain.navigation.benachrichtigungen.krankmeldungen.title',
                    )}
                  </p>
                </ListItem>
              )}
            {manager.rechte.mitarbeiter.fristen &&
              benachrichtigungen.fristen.length > 0 && (
                <ListItem
                  count={benachrichtigungen.fristen.length}
                  onClick={() => setActiveSubMenu('fristen')}>
                  <FontAwesomeIcon icon={faAlarmExclamation} />
                  <p>{t('domain.navigation.benachrichtigungen.fristen')}</p>
                </ListItem>
              )}
            {manager.rechte.mitarbeiter.dokumente &&
              benachrichtigungen.dokumente.length > 0 && (
                <ListItem
                  count={benachrichtigungen.dokumente.length}
                  onClick={() => setActiveSubMenu('dokumente')}>
                  <FontAwesomeIcon icon={faFile} />
                  <p>{t('domain.navigation.benachrichtigungen.dokumente')}</p>
                </ListItem>
              )}
            {manager.rechte.mitarbeiter.erlaubt &&
              benachrichtigungen.personalfragebogen.length > 0 && (
                <ListItem
                  count={benachrichtigungen.personalfragebogen.length}
                  onClick={() => setActiveSubMenu('personalfragebogen')}>
                  <FontAwesomeIcon icon={faPaperPlane} />
                  <p>
                    {t(
                      'domain.navigation.benachrichtigungen.personalfragebogen',
                    )}
                  </p>
                </ListItem>
              )}
            {manager.rechte.dienstplan.mitarbeiterEinteilen &&
              benachrichtigungen.schichttauschAntraege.length > 0 && (
                <ListItem
                  count={benachrichtigungen.schichttauschAntraege.length}
                  onClick={() => setActiveSubMenu('schichttausch')}>
                  <FontAwesomeIcon icon={faArrowLeftArrowRight} />
                  <p>
                    {t(
                      'domain.navigation.benachrichtigungen.schichttauschAntraege.title',
                    )}
                  </p>
                </ListItem>
              )}
          </div>
        ) : (
          <Transition
            key={activeSubMenu}
            show={activeSubMenu !== null}
            appear
            enter="transition transform duration-300 ease-in-out"
            enterFrom="translate-x-full opacity-0"
            enterTo="translate-x-0 opacity-100"
            leave="transition transform duration-300 ease-in-out"
            leaveFrom="translate-x-0 opacity-100"
            leaveTo="translate-x-full opacity-0">
            <nav className="px-4 flex flex-col gap-4 overflow-scroll">
              {activeSubMenu === 'fehlendeArbeitszeiten' && (
                <div onClick={() => handleClose()}>
                  {benachrichtigungen.fehlendeArbeitszeiten.map((b, idx) => (
                    <SubListItem
                      key={b.id}
                      isLast={
                        benachrichtigungen.fehlendeArbeitszeiten.length - 1 ===
                        idx
                      }>
                      <FehlendeArbeitszeitDetail
                        benachrichtigung={b}
                        key={b.id}
                      />
                    </SubListItem>
                  ))}
                </div>
              )}
              {activeSubMenu === 'abwesenheitsAntraege' && (
                <div onClick={() => handleClose()}>
                  {benachrichtigungen.abwesenheitsantraege.map((b, idx) => (
                    <SubListItem
                      onClick={() => handleClose()}
                      key={b.id}
                      isLast={
                        benachrichtigungen.abwesenheitsantraege.length - 1 ===
                        idx
                      }>
                      <AbwesenheitsantragDetail
                        benachrichtigung={b}
                        key={b.id}
                      />
                    </SubListItem>
                  ))}
                </div>
              )}
              {activeSubMenu === 'krankmeldungen' && (
                <div onClick={() => handleClose()}>
                  {benachrichtigungen.krankmeldungen.map((b, idx) => (
                    <SubListItem
                      onClick={() => handleClose()}
                      key={idx}
                      isLast={
                        benachrichtigungen.krankmeldungen.length - 1 === idx
                      }>
                      <KrankmeldungenDetail benachrichtigung={b} />
                    </SubListItem>
                  ))}
                </div>
              )}
              {activeSubMenu === 'fristen' &&
                benachrichtigungen.fristen.map((b, idx) => (
                  <SubListItem
                    key={b.id}
                    isLast={benachrichtigungen.fristen.length - 1 === idx}>
                    <FristenDetail benachrichtigung={b} key={b.id} />
                  </SubListItem>
                ))}
              {activeSubMenu === 'dokumente' &&
                benachrichtigungen.dokumente.map((b, idx) => (
                  <SubListItem
                    key={b.id}
                    isLast={benachrichtigungen.dokumente.length - 1 === idx}>
                    <DokumenteDetail benachrichtigung={b} key={b.id} />
                  </SubListItem>
                ))}
              {activeSubMenu === 'personalfragebogen' &&
                benachrichtigungen.personalfragebogen.map((b, idx) => (
                  <SubListItem
                    key={b.id}
                    isLast={
                      benachrichtigungen.personalfragebogen.length - 1 === idx
                    }>
                    <PersonalfragebogenDetail benachrichtigung={b} />
                  </SubListItem>
                ))}
              {activeSubMenu === 'schichttausch' &&
                benachrichtigungen.schichttauschAntraege.map((b, idx) => (
                  <SubListItem
                    key={b.id}
                    isLast={
                      benachrichtigungen.schichttauschAntraege.length - 1 ===
                      idx
                    }
                    onClick={() => {
                      setActiveAntrag(b);
                      setActiveSubMenu('schichttauschAntrag');
                    }}>
                    <SchichttauschDetail benachrichtigung={b} />
                  </SubListItem>
                ))}
              {activeSubMenu === 'schichttauschAntrag' && activeAntrag && (
                <AntragsContent antrag={activeAntrag} />
              )}
            </nav>
          </Transition>
        )}
      </SheetContent>
    </Sheet>
  );
}
